import moment from "moment";

import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { Guid } from "@/Common/Models/Guid";
import { Comment } from "@/Common/Models/Patient/Comment";
import { LabRequestBillCode, LabRequestType } from "@/Common/Models/Patient/LabRequest";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { ExamRequestPriorityOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { IMedicalRequest } from "./IMedicalRequest";
import { RequestsInboxItem } from "./MyNotesInboxItem";

class LabRequest implements IMedicalRequest {
    public id: Guid;
    public requestingStaffContactFacetId: Guid;
    public isSupervisorRequired: boolean;
    public priorityDropDownId: string;
    public reason: string;
    public isFaxed: boolean;
    public faxDate: Date;
    public isPrinted: boolean;
    public requestDate: Date;
    public encounterId: Guid;
    public isInvalid: boolean;
    public statusLookupId: string;
    public firstPrintedByEmployeeId: Guid;
    public firstPrintedDate: Date;
    public dueDate: Date;
    public dueDateText: string;
    public statusChangeDate: Date;
    public isVisibleInCPP: boolean;

    public request: string;
    public interfaceRequestId: string;
    public risRequestId: string;
    public sendStatus: string;

    public createdByContactId: string;
    public lastUpdatedByContactId: string;
    public lastUpdateDate: Date;
    public type: LabRequestType = LabRequestType.None;
    public sharedItemId: string;
    public sharedItem: SharedItem;
    public sharedItemMessages: SharedItemMessage[];
    public signedPrintAuthorizations: SignedPrintAuthorization[];
    public attachedDocuments: AttachedObjectResponse[];
    public patientContactId: string;
    public recipient: string | null;
    public recipientContactDirectoryEntityId: string | null;
    public recipientContactDirectoryEntityType: DirectoryEntityType | null;
    public recipientDirectorySelectedEntity: DirectorySelectableEntity<IDirectoryEntity> | null;
    public recipientSearchText: string;

    constructor(fields?: Partial<LabRequest>) {
        Object.assign(this, fields);
    }

    /**
     * Provides a common field for a medical request's body text between classes implementing IMedicalRequest
     * */
    public get description(): string {
        return this.request;
    }
    public set description(_description: string) {
        this.request = _description;
    }

    private _billCodes: LabRequestBillCode[];
    public get billCodes(): LabRequestBillCode[] {
        return this._billCodes ? this._billCodes : [];
    }
    public set billCodes(_billCodes: LabRequestBillCode[]) {
        this._billCodes = _billCodes ? _billCodes : [];
    }

    private _comments: Comment[];

    public get comments(): Comment[] {
        return this._comments || [];
    }
    public set comments(comments: Comment[]) {
        this._comments = comments || [];
    }

    public getPriority(): ExamRequestPriorityOption {
        return ExamRequestPriorityOption.fromDropDownId(this.priorityDropDownId);
    }

    public isUrgent(): boolean {
        return ExamRequestPriorityOption.Urgent.model.Id === this.priorityDropDownId;
    }

    public getStatusLocalizedValue(): string {
        return this.statusLookupId ? cacheStore.GetLocalizedValueByLookupId(this.statusLookupId) : "";
    }

    public static fromComposite(item: RequestsInboxItem): LabRequest {
        if (!item)
            return null;

        return new LabRequest({
            id: item.RequestId,
            requestingStaffContactFacetId: item.RequestingStaffContactFacetId,
            isSupervisorRequired: item.IsSupervisorRequired,
            priorityDropDownId: item.PriorityDropdownId || ExamRequestPriorityOption.Normal.model.Id,
            reason: item.Reason,
            isFaxed: item.IsFaxed,
            isPrinted: item.IsPrinted,
            requestDate: item.RequestDate,
            encounterId: item.EncounterId,
            description: item.Request,
            lastUpdatedByContactId: item.LastUpdateByContactId,
            risRequestId: item.RISRequestId,
            type: item.Type,
            sendStatus: item.SendStatus,
            billCodes: item.LabRequestBillCodes,
            isInvalid: item.IsInvalid,
            firstPrintedByEmployeeId: item.FirstPrintedByEmployeeId,
            firstPrintedDate: item.FirstPrintedDate,
            statusLookupId: item.StatusLookupId,
            comments: item.Comments,
            dueDate: item.DueDate,
            dueDateText: item.DueDate ? moment(item.DueDate).format("YYYY-MM-DD") : "",
            statusChangeDate: item.StatusChangeDate,
            isVisibleInCPP: item.IsVisibleInCPP,
            createdByContactId: item.CreatedByContactId,
            sharedItemId: item.SharedItemId,
            sharedItem: item.SharedItem,
            sharedItemMessages: item.SharedItemMessages,
            attachedDocuments: item.AttachedDocuments,
            signedPrintAuthorizations: item.SignedPrintAuthorizations,
            patientContactId: item.ContactId,
            recipient: item.Recipient,
            recipientContactDirectoryEntityId: item.RecipientContactDirectoryEntityId,
            recipientContactDirectoryEntityType: item.RecipientContactDirectoryEntityType,
            recipientDirectorySelectedEntity: item.RecipientDirectorySelectedEntity
        });
    }
}

export { LabRequest };
